import { type JSX, type ReactNode } from "react";
import { cn } from "@openreel/ui";
import { OnboardingWizard } from "./OnboardingWizard";
import { SignInScreen } from "./SignInScreen";
import { StepShell } from "./StepShell";
import {
  FOCUS_RING,
  PILL_BASE,
  PILL_DARK,
  PILL_MOTION,
  SURFACE_BG,
} from "./surface";
// The auth service lane owns this module. It is the only runtime dependency
// this folder has on the network layer; every other component here takes what
// it needs as props.
import { useAuth } from "../../services/auth";

export interface OnboardingGateProps {
  /** Rendered once the visitor is signed in and onboarding is complete. */
  children: ReactNode;
}

function AuthSplash(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center",
        SURFACE_BG,
      )}
    >
      <span className="sr-only">Loading your account</span>
      <span
        aria-hidden
        className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white motion-safe:animate-spin"
      />
    </div>
  );
}

/**
 * Gates the app on auth state: sign-in screen while signed out, the onboarding
 * wizard until onboarding is finished, the app itself after that.
 */
export function OnboardingGate({ children }: OnboardingGateProps): JSX.Element {
  const {
    user,
    onboarding,
    isLoading,
    isOffline,
    refresh,
    signInWithGoogle,
    requestEmailOtp,
    verifyEmailOtp,
    updateOnboarding,
    updateProfile,
  } = useAuth();

  if (isLoading) {
    return <AuthSplash />;
  }

  // A request that never got a server verdict is not proof of being signed
  // out, so offer a retry instead of a sign-in wall.
  if (isOffline && !user) {
    return (
      <StepShell
        heading="We could not reach flshy"
        subheading="Your session could not be checked. This is usually a connection problem, not a sign-in problem."
        announcement="We could not reach flshy. Try again."
        footer={
          <button
            type="button"
            onClick={() => void refresh()}
            className={cn(PILL_BASE, PILL_MOTION, FOCUS_RING, PILL_DARK)}
          >
            Try again
          </button>
        }
      />
    );
  }

  if (!user) {
    return (
      <SignInScreen
        onGoogle={signInWithGoogle}
        onRequestOtp={requestEmailOtp}
        // The store populates the session on success; the screen only needs to
        // know the verify resolved, so the result is discarded here.
        onVerifyOtp={async (email, code) => {
          await verifyEmailOtp(email, code);
        }}
      />
    );
  }

  if (!onboarding?.completed) {
    return (
      <OnboardingWizard
        user={user}
        initialOnboarding={onboarding}
        // The store writes the server's reply back into `onboarding`, so
        // finishing the flow re-renders this gate straight into the app.
        patchOnboarding={updateOnboarding}
        // A brand-new account has no name yet, so the wizard opens on a name
        // step and saves it here before the onboarding questions.
        updateProfile={updateProfile}
      />
    );
  }

  return <>{children}</>;
}
